/**
 * Theme Context
 * Provides global theme management and persistence
 *
 * @module contexts/ThemeContext
 */

import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    useEffect,
    ReactNode,
} from 'react';
import { AppTheme } from '../types';

// =============================================================================
// Constants
// =============================================================================

const THEME_KEY = 'notespace_theme_v1';

// =============================================================================
// Types
// =============================================================================

interface ThemeContextValue {
    /** Current theme setting */
    theme: AppTheme;
    /** Whether dark mode is currently active (resolved from system if theme is 'system') */
    isDark: boolean;
    /** Set the theme */
    setTheme: (theme: AppTheme) => void;
    /** Toggle between light and dark (ignores system) */
    toggleTheme: () => void;
}

// =============================================================================
// Helpers
// =============================================================================

function getSystemTheme(): 'light' | 'dark' {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: AppTheme): 'light' | 'dark' {
    if (theme === 'system') {
        return getSystemTheme();
    }
    return theme;
}

function applyThemeToDocument(resolvedTheme: 'light' | 'dark'): void {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
        metaThemeColor.setAttribute(
            'content',
            resolvedTheme === 'dark' ? '#0f172a' : '#ffffff'
        );
    }
}

// =============================================================================
// Context
// =============================================================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface ThemeProviderProps {
    children: ReactNode;
    defaultTheme?: AppTheme;
}

export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
    // Initialize theme from localStorage or default
    const [theme, setThemeState] = useState<AppTheme>(() => {
        if (typeof window === 'undefined') return defaultTheme;
        const stored = localStorage.getItem(THEME_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
            return stored;
        }
        return defaultTheme;
    });

    const [isDark, setIsDark] = useState(() => resolveTheme(theme) === 'dark');

    // Set theme and persist
    const setTheme = useCallback((newTheme: AppTheme) => {
        setThemeState(newTheme);
        localStorage.setItem(THEME_KEY, newTheme);
    }, []);

    // Toggle between light and dark
    const toggleTheme = useCallback(() => {
        setTheme(isDark ? 'light' : 'dark');
    }, [isDark, setTheme]);

    // Apply theme changes
    useEffect(() => {
        const resolved = resolveTheme(theme);
        setIsDark(resolved === 'dark');
        applyThemeToDocument(resolved);
    }, [theme]);

    // Listen for system theme changes when using 'system'
    useEffect(() => {
        if (theme !== 'system') return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = (e: MediaQueryListEvent) => {
            const resolved = e.matches ? 'dark' : 'light';
            setIsDark(resolved === 'dark');
            applyThemeToDocument(resolved);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    const value = useMemo<ThemeContextValue>(
        () => ({
            theme,
            isDark,
            setTheme,
            toggleTheme,
        }),
        [theme, isDark, setTheme, toggleTheme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access the theme context
 * @throws Error if used outside of ThemeProvider
 */
export function useTheme(): ThemeContextValue {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

/**
 * Hook to access just the isDark value
 */
export function useIsDark(): boolean {
    const { isDark } = useTheme();
    return isDark;
}
